import { createSlice } from "@reduxjs/toolkit";

export const PROMO_BANNER_STORAGE_KEY = "flots_promo_banner_dismissed";

/**
 * Attribute put on `<html>` once the current offer is dismissed. globals.css hides the
 * banner and zeroes `--promo-h` from it, so a dismissed banner never paints on reload.
 */
export const PROMO_DISMISSED_ATTR = "data-promo-dismissed";

/**
 * Identifies the current offer. Bump it when the promo changes so visitors who
 * dismissed the previous banner are shown the new one.
 */
export const PROMO_BANNER_ID = "2-months-free";

type PromoBannerState = {
  dismissed: boolean;
};

function readDismissed(): boolean {
  if (typeof window === "undefined") return false;

  try {
    return (
      window.localStorage.getItem(PROMO_BANNER_STORAGE_KEY) === PROMO_BANNER_ID
    );
  } catch {
    // Storage blocked (private mode, cookie settings) — just show the banner.
    return false;
  }
}

function writeDismissed() {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(PROMO_BANNER_STORAGE_KEY, PROMO_BANNER_ID);
  } catch {
    // Nothing to persist to — the banner comes back on the next visit.
  }
}

// The server always renders the banner (it can't read localStorage), so the
// initial state must match that or hydration would mismatch. The pre-paint
// script in app/layout.tsx hides it with CSS until this state catches up.
const initialState: PromoBannerState = {
  dismissed: false,
};

const promoBannerSlice = createSlice({
  name: "promoBanner",
  initialState,
  reducers: {
    hydrate: (state) => {
      state.dismissed = readDismissed();
    },
    dismiss: (state) => {
      state.dismissed = true;
      writeDismissed();
    },
  },
});

export const { hydrate, dismiss } = promoBannerSlice.actions;
export default promoBannerSlice.reducer;

export type { PromoBannerState };
